import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface Aporte {
    persona: {
        apellidos: string;
        nombres: string;
        cuil: number;
    };
    cargo: {
        cargo: string;
        escalafon: string;
        agrup: string;
    };
    importes: Array<{
        cod: string;
        importe: number;
        porc: number | null;
    }>;
}

export const DataSummary = ({ data }: { data: Aporte[] }) => {
    const [openSections, setOpenSections] = useState({
        cargos: true,
        importes: true,
        personal: false
    });

    const toggleSection = (section: keyof typeof openSections) => {
        setOpenSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

  const cargoCounts = data.reduce((acc, record) => {
        const cargoKey = `${record.cargo.escalafon}-${record.cargo.agrup}-${record.cargo.cargo}`;
        acc[cargoKey] = (acc[cargoKey] || 0) + 1;
        return acc;
    }, {} as Record<string, number>);

    const totalImportes = data.reduce((acc, record) => {
        record.importes.forEach(importe => {
            acc[importe.cod] = (acc[importe.cod] || 0) + importe.importe;
        });
      return acc;
  }, {} as Record<string, number>);

    const SectionHeader = ({ title, isOpen, onToggle }: { title: string; isOpen: boolean; onToggle: () => void }) => (
        <div 
            className="flex items-center justify-between cursor-pointer bg-gray-50 px-4 py-2 rounded-t-lg"
            onClick={onToggle}
        >
            <h2 className="text-xl font-bold">{title}</h2>
            {isOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </div>
    );

  return (
        <div className="mt-8 space-y-6">
            <div className="bg-white shadow rounded-lg">
                <SectionHeader 
                    title="Resumen de Cargos" 
                    isOpen={openSections.cargos}
                    onToggle={() => toggleSection('cargos')}
                />
                {openSections.cargos && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Cargo
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Cantidad
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
              {Object.entries(cargoCounts).map(([cargo, count]) => (
                                    <tr key={cargo}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {cargo}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {count} registros
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="bg-white shadow rounded-lg">
                <SectionHeader 
                    title="Resumen de Importes" 
                    isOpen={openSections.importes}
                    onToggle={() => toggleSection('importes')}
                />
                {openSections.importes && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Concepto
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Total
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {Object.entries(totalImportes).map(([cod, total]) => (
                                    <tr key={cod}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {getConceptoDescripcion(cod)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            ${total.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="bg-white shadow rounded-lg">
                <SectionHeader 
                    title="Listado de Personal" 
                    isOpen={openSections.personal}
                    onToggle={() => toggleSection('personal')}
                />
                {openSections.personal && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Apellido y Nombre
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        CUIL
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Cargo
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Remunerativo
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        No Remunerativo
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        % Aporte
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        % Contrib.
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {data.map((record, index) => {
                                    const remunerativo = record.importes.find(i => i.cod === '50')?.importe || 0;
                                    const noRemunerativo = record.importes.find(i => i.cod === '51')?.importe || 0;
                                    const pjeAporte = record.importes.find(i => i.cod === '01')?.porc || 0;
                                    const pjeContrib = record.importes.find(i => i.cod === '02')?.porc || 0;

                                    return (
                                        <tr key={index}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {record.persona.apellidos}, {record.persona.nombres}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {record.persona.cuil}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {`${record.cargo.escalafon}-${record.cargo.agrup}-${record.cargo.cargo}`}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                ${remunerativo.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                ${noRemunerativo.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {pjeAporte}%
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {pjeContrib}%
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
      </div>
  );
};

function getConceptoDescripcion(cod: string): string {
    const conceptos: Record<string, string> = {
        '01': 'Aportes',
        '02': 'Contribuciones',
        '50': 'Remunerativo',
        '51': 'No Remunerativo',
        '52': 'Asignaciones Familiares'
    };
    return conceptos[cod] || `Concepto ${cod}`;
}